import { useState, useEffect } from "react";
import { Lock, Minus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, money, money0 } from "../lib/api";
import { PLAN_LABEL, partySize } from "../lib/tiers";
import { ScholarshipPicker } from "./ScholarshipPicker";

export const JoinDialog = ({ open, onOpenChange, ts, membership, onConfirm }) => {
  const renewing = membership?.status === "active";
  const [plan, setPlan] = useState(renewing ? membership.plan : ts.plans[0]);
  const [extra, setExtra] = useState(0);
  const [autopay, setAutopay] = useState(true);
  const [level, setLevel] = useState("standard");
  const [ack, setAck] = useState(false);
  const [companion, setCompanion] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [school, setSchool] = useState(membership?.school_id || "");
  const [addon, setAddon] = useState(membership?.scholarship_addon || 0);
  const usePlan = renewing ? membership.plan : plan;
  const useExtra = renewing ? membership.extra_people : extra;
  const useAuto = renewing ? false : autopay;
  const useLevel = renewing ? membership.level : level;
  const [q, setQ] = useState(null);
  useEffect(() => {
    api.post("/pricing/quote", { tier: ts.tier, plan: usePlan, level: useLevel, extra_people: useExtra, autopay: useAuto })
      .then((r) => setQ(r.data)).catch(() => setQ(null));
  }, [ts.tier, usePlan, useLevel, useExtra, useAuto]);
  const fee = q ? Math.round((q.fee + (addon || 0)) * 100) / 100 : undefined;
  const trip = q?.trip;
  const promoPct = q?.promo_pct || 0;

  const go = async () => {
    setBusy(true);
    try { await onConfirm({ acknowledge_self_funding: renewing || ack, plan: usePlan, level: useLevel, extra_people: useExtra, autopay: useAuto, companion_name: companion, notes, school_id: school, scholarship_addon: addon || 0 }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pay-dialog" className="bg-[#13192B] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-lux text-3xl font-medium">{renewing ? "Renew early" : `Join ${ts.label}`}</DialogTitle>
          <DialogDescription className="text-slate-400">You'll be taken to Stripe's secure checkout to pay.</DialogDescription>
        </DialogHeader>
        {!renewing && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-white/5 border border-white/10">
              {["standard", "premium"].map((l) => (
                <button key={l} data-testid={`level-${l}`} onClick={() => setLevel(l)}
                  className={`rounded-full py-2 text-sm font-semibold transition-colors ${level === l ? (l === "premium" ? "bg-gradient-to-r from-[#A855F7] to-[#F43F5E] text-white" : "bg-[#38BDF8] text-[#0B0F19]") : "text-slate-300"}`}>
                  {l === "premium" ? `Premium · ${ts.premium_trip_multiplier}× trip` : "Standard"}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {ts.plans.map((p) => (
                <button key={p} data-testid={`plan-${p}`} onClick={() => setPlan(p)}
                  className={`rounded-xl border p-3 text-left transition-colors ${plan === p ? "border-[#38BDF8] bg-[#38BDF8]/10" : "border-white/10"}`}>
                  <p className="text-sm">{PLAN_LABEL[p]}</p>
                  <p className="font-mono-lux text-xs text-[#38BDF8] mt-1">{money(level === "premium" ? ts.plan_prices[p].premium_fee : ts.plan_prices[p].fee)}</p>
                </button>
              ))}
            </div>
            {plan === "family" && (
              <div className="flex items-center justify-between lux-card p-3">
                <span className="text-sm">Extra people <span className="text-xs text-slate-500">(+{money(ts.extra_person_fee)}/mo each)</span></span>
                <div className="flex items-center gap-3">
                  <button data-testid="extra-people-minus" onClick={() => setExtra(Math.max(0, extra - 1))} className="btn-ghost w-8 h-8 grid place-items-center"><Minus className="w-3 h-3" /></button>
                  <span data-testid="extra-people-count" className="font-mono-lux w-4 text-center">{extra}</span>
                  <button data-testid="extra-people-plus" onClick={() => setExtra(Math.min(6, extra + 1))} className="btn-ghost w-8 h-8 grid place-items-center"><Plus className="w-3 h-3" /></button>
                </div>
              </div>
            )}
            {plan !== "solo" && (
              <label className="block"><span className="text-xs text-slate-400">Who's traveling with you? (names)</span>
                <input data-testid="join-companions-input" value={companion} onChange={(e) => setCompanion(e.target.value)} className="lux-input mt-1" placeholder="e.g. Sam, Ella & Noah" /></label>
            )}
            {ts.notes_label && (
              <label className="block"><span className="text-xs text-slate-400">{ts.notes_label}</span>
                <textarea data-testid="join-notes-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="lux-input mt-1"
                  placeholder={ts.tier === "accessible" ? "e.g. wheelchair user, roll-in shower, service animal" : "e.g. 6 and 3"} /></label>
            )}
            <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
              <input data-testid="pay-autopay-checkbox" type="checkbox" checked={autopay} onChange={(e) => setAutopay(e.target.checked)} className="accent-[#38BDF8] w-4 h-4" />
              Pay automatically each month and save {ts.autopay_discount_pct}%
            </label>
          </div>
        )}
        <ScholarshipPicker school={school} setSchool={setSchool} addon={addon} setAddon={setAddon} />
        <div className="lux-card p-5 flex items-center gap-4">
          <Lock className="w-6 h-6 text-[#38BDF8]" />
          <div className="flex-1">
            <p className="text-sm">{useLevel === "premium" ? "Premium · " : ""}{partySize(usePlan, useExtra)} traveler{partySize(usePlan, useExtra) > 1 ? "s" : ""} · {money0(trip)} trip</p>
            <p className="text-xs text-slate-500">{useAuto ? "Monthly autopay · cancel anytime" : "One-time · 30 days"}</p>
            {addon > 0 && <p data-testid="pay-dialog-addon" className="text-xs text-emerald-300 mt-1">Includes {money(addon)} scholarship gift</p>}
            {q?.price_locked && <p data-testid="pay-dialog-price-lock" className="text-xs text-[#38BDF8] mt-1">Founder price lock applied</p>}
            {promoPct > 0 && <p data-testid="pay-dialog-promo" className="text-xs text-[#38BDF8] mt-1">Founding {promoPct}% off applied</p>}
          </div>
          <p data-testid="pay-dialog-amount" className="font-mono-lux text-xl text-[#38BDF8]">{money(fee)}</p>
        </div>
        {!renewing && (
          <div className="rounded-2xl border border-amber-300/30 bg-amber-300/5 p-4 space-y-3">
            <p data-testid="self-funded-estimate" className="text-sm text-slate-300">
              If sign-ups stopped, your own payments would fully cover your {money(trip)} trip in about{" "}
              <span className="font-mono-lux text-amber-200">{q?.self_funded_months ?? "…"} months</span> (less if the fund already has money for you).
            </p>
            <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
              <input data-testid="self-funding-ack-checkbox" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="accent-[#38BDF8] w-4 h-4 mt-0.5" />
              I understand that if new sign-ups slow down or stop, I may be paying for my own trip, minus whatever is already in the fund, and I may keep paying until it's covered.
            </label>
          </div>
        )}
        <button data-testid="pay-confirm-btn" onClick={go} disabled={busy || !q || !school || (!renewing && !ack)} className="btn-gold w-full py-3.5">
          {busy ? "Redirecting to Stripe…" : `Continue to payment · ${money(fee)}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
